<template>
  <div>
    <v-text-field label="Suche" variant="filled" v-model="fulltextSearch" append-inner-icon="mdi-magnify" @click:append-inner="doSearch"
      @keypress.enter="doSearch" hide-details :loading="contactHistoryStore.loading" />
    <v-container v-if="!contactHistoryStore.loading && contactHistoryStore.displayFilters.length > 0" fluid>
      <filter-list :store="contactHistoryStore" />
    </v-container>
    <div v-if="!contactHistoryStore.loading && !contactHistoryStore.error">
      <v-list>

        <template v-for="contactHistory in contactHistoryStore.data" :key="contactHistory.Nr">
          <v-list-item link :to="{
            name: 'contact_histories_details',
            params: {
              id: contactHistory.KundenNr,
              contact_history_id: contactHistory.Nr,
              name: kundenName,
            },
          }">
            <template #prepend>
              <v-icon color="primary">{{ contactHistory.typeIcon }}</v-icon>
            </template>

              <v-list-item-title>{{ contactHistory.Thema }}</v-list-item-title>
              <v-list-item-subtitle>{{ contactHistory.AnlageDatumFormatted }}</v-list-item-subtitle>
              <v-list-item-subtitle v-if="contactHistory.Ansprechpartner.length > 0">{{
                contactHistory.AnsprechpartnerFormatted
              }}</v-list-item-subtitle>

            <template #append>
              <v-list-item-action>
                <v-icon :color="contactHistory.color">{{ contactHistory.icon }}</v-icon>
              </v-list-item-action>
            </template>
          </v-list-item>
          <hr
            role="separator"
            aria-orientation="horizontal"
            class="v-divider"
            theme="light"
          />
        </template>

      </v-list>
      <v-container fluid class="mb-10">
        <pagination :module="contactHistoryStore.$id" />
      </v-container>
    </div>
    <template v-else-if="contactHistoryStore.error">
      <v-container class="py-1" fluid>
        <error :module="contactHistoryStore.$id" />
      </v-container>
    </template>
    <v-btn icon style="z-index: 1;"
      position="fixed"
      location="end bottom"
      class="ma-3"
      :to="{
        name: 'contact_history_create',
        params: { id: route.params.id },
        query: { ansprechpartner_nr: route.query.ansprechpartner_nr},
      }"
      color="primary">
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </div>
</template>

<script lang="ts" setup>
import FilterList from '@/components/FilterList.vue';
import Pagination from '@/components/Pagination.vue';
import Error from '@/components/Error.vue';
import {computed, ref, watch} from 'vue';
import {useStorage} from '@/composables/storage';
import {useRoute} from 'vue-router';
import {useContactHistoryStore} from '@/store/contactHistories';
import {useFilterSort} from '@/composables/filterSort';
import {ContactHistoryFilters} from '@/model/ContactHistory';

const route = useRoute();
const emit = defineEmits({'close': () => true});
const contactHistoryStore = useContactHistoryStore();
const {save, filters} = useFilterSort(contactHistoryStore, emit);
useStorage(
    contactHistoryStore,
    (contactHistories, route): string | null => {
      if (route?.params.name) return Array.isArray(route.params.name) ? route.params.name[0] : route.params.name;
      if (route?.query.ansprechpartner_nr) {
        // "contactHistories" is either empty, or filled with items where ansprechpartner_nr is one of the ansprechpartners
        if (contactHistories && contactHistories.length > 0) {
          const index = contactHistories[0].Ansprechpartner.findIndex((partner) => {
            return partner.AnsprechpartnerNr === Number(route.query.ansprechpartner_nr);
          });
          return `Kontakte ${contactHistories[0].Ansprechpartner[index].Nachname}, ${contactHistories[0].Ansprechpartner[index].Vorname}`;
        } else {
          return 'Kontakte Ansprechpartner';
        }
      }
      if (route?.params.name) {
        return `Kontakte mit ${route?.params.name}`;
      }
      return 'Kontakte';
    },
    (route, _originalFilters, defaultFilters): ContactHistoryFilters => {
      if (route.query.ansprechpartner_nr) {
        return {
          ...defaultFilters,
          KundenNr: parseInt(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id),
          Ansprechpartner$Any: {
            AnsprechpartnerNr: Number(route.query.ansprechpartner_nr),
          },
        };
      }
      return {
        ...defaultFilters,
        KundenNr: parseInt(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id),
      };
    },
);
const fulltextSearch = computed({
  get(): string {
    if (filters.value.Fulltext$Fulltext) {
      return filters.value.Fulltext$Fulltext.words.join(' ');
    } else return '';
  },
  set(val: string) {
    if (!val) {
      filters.value.Fulltext$Fulltext.words = [];
      return;
    }
    filters.value.Fulltext$Fulltext.words = val.split(' ');
  },
});

function doSearch() {
  if (searchDebounceTimeout.value) clearTimeout(searchDebounceTimeout.value);
  save();
};

const searchDebounceTimeout = ref<NodeJS.Timeout | null>(null);
const kundenName = ref('');
kundenName.value = Array.isArray(route.params.name) ? route.params.name[0] : route.params.name; // onCreated()

watch(fulltextSearch, (newVal: string, oldVal: string) => {
  if (newVal === oldVal || newVal.length < 3) return;
  if (searchDebounceTimeout.value) clearTimeout(searchDebounceTimeout.value);

  searchDebounceTimeout.value = setTimeout(() => {
    doSearch();
  }, 500);
});

defineExpose({
  route,
  emit,
  filters, save,
  contactHistoryStore,
  fulltextSearch,
  doSearch,
  searchDebounceTimeout,
  kundenName,
  Error,
  Pagination,
  FilterList,
});
</script>
